import { IS_ALPHA_STAGE, SESSION_COOKIE_DOMAIN } from "../api-config.mjs";

export const SESSION_COOKIE_NAME = IS_ALPHA_STAGE ? "sid_alpha" : "sid";

// Thirty days, matching SESSION_LENGTH on the backend session row and the
// Max-Age cca/user.py puts on its own Set-Cookie.
const SESSION_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

// Whether a Domain=<stage domain> cookie can be written from this document.
// The browser silently drops a cookie whose Domain does not cover the page
// host, so on localhost (yarn develop, the local integration-test server)
// there is nothing to do -- and nothing lost, because local dev authenticates
// media.* with the ?token= query param instead (api-config appendDevToken).
const canWriteSessionCookie = () =>
  isBrowser() &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost";

// Mirror the session id into the stage session cookie (sid / sid_alpha) with the
// SAME Domain, Path and SameSite the backend's /a2/login response uses, so the
// api cluster's AuthorizationFilter authenticates cross-host sub-resource
// requests -- <audio src="https://media.<domain>/audio?...">. The backend already
// sets this cookie on login; the mirror exists for the sessions that reach the
// browser WITHOUT that response: a session restored from localStorage after the
// login-time cookie aged out, and a session injected by the integration tests
// (helper.js loginAsRandomUser). Both show a logged-in UI whose audio would
// otherwise 401.
//
// Not HttpOnly on the backend either (cca/user.py), which is what makes
// re-writing it from script possible at all.
export const setSessionCookie = (sessionId) => {
  if (!sessionId || !canWriteSessionCookie()) {
    return;
  }
  document.cookie =
    `${SESSION_COOKIE_NAME}=${sessionId}; ` +
    `Max-Age=${SESSION_COOKIE_MAX_AGE_SECONDS}; ` +
    `Domain=${SESSION_COOKIE_DOMAIN}; SameSite=Strict; Path=/; Secure`;
};

// Counterpart for logout. deleteCookie() below writes a host-only cookie and so
// cannot reach a Domain-scoped one; only an expired cookie with the same Domain
// removes it.
export const clearSessionCookie = () => {
  if (!canWriteSessionCookie()) {
    return;
  }
  document.cookie =
    `${SESSION_COOKIE_NAME}=; Max-Age=0; ` +
    `Domain=${SESSION_COOKIE_DOMAIN}; SameSite=Strict; Path=/; Secure`;
};

export const isBrowser = () => {
    // During nodejs build time, localStorage doesn't exist.
    if (typeof localStorage === 'undefined') {
      return false;
    }
    return true;
};

// https://www.tabnine.com/academy/javascript/how-to-set-cookies-javascript/
export const setCookie = (cName, cValue, expDays) => {
  let date = new Date();
  date.setTime(date.getTime() + (expDays * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${cName}=${cValue}; ${expires}; path=/`;
};

export function getCookie(cname) {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(';');
  for(let i = 0; i <ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};

export function deleteCookie(cname) {
  setCookie(cname, getCookie(cname), 0);
};

// Send the browser to another site with a full page load. Not `navigate` from
// components/Router: that is Gatsby's client-side router and only knows this site's own
// routes. Its own function so a unit test can stand in for it, since jsdom does not let a
// test replace window.location.assign.
export const leaveSiteTo = (url) => {
  window.location.assign(url);
};

// Reload the current page. Its own function for the same reason as leaveSiteTo: jsdom
// does not let a test replace window.location.reload.
export const reloadPage = () => {
  window.location.reload();
};

export const isMobileDevice = () => {
  if (isBrowser()) {
    return window.innerWidth < 991;
  }
  return true;
};
